import React from 'react'

import { ROLES } from 'constants/roles'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useInternalRoles } from 'hooks/api'
import { useInviteInternalUser } from 'hooks/api/useInviteInternalUser'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Role } from 'types'

import { Stack, Typography } from '@mui/material'

import { Button, Modal, TextField } from '@repo/ui'
import { SelectCompanyField, Select, type SelectItem } from 'components'


interface InviteInternalUserModalProps {
  companyId?: string
  type: 'platform' | 'company'
  open: boolean
  onClose: () => void
  onInvited?: () => void
}

export const InviteInternalUserModal = ({ companyId, type, open, onClose, onInvited }: InviteInternalUserModalProps) => {
  const methods = useForm({
    defaultValues: {
      email: '',
      role: '',
      company_ids: [] as string[],
    },
  })
  const params = useParams()
  const { control, handleSubmit, setValue, watch } = methods

  const selectedRoleName = watch('role') as string
  const isPlatformManager = selectedRoleName === 'platform_manager'
  const isPlatformAdmin = selectedRoleName === 'platform_admin'

  const { open: notification } = useSnackbar()

  const { mutate: invite, isPending: isLoading } = useInviteInternalUser()

  const handleInvite = (payload: { role_name: string; email: string; company_ids?: string[] }) => {
    invite(
      {
        role_name: payload.role_name as 'platform_admin' | 'platform_manager',
        email: payload.email,
        company_ids: payload.company_ids,
      },
      {
        onSuccess: () => {
          notification({
            type: 'success',
            title: 'Member has been invited.',
          })
          internalOnClose()
        },
        onError: error => {
          const message = getErrorMessage(error)
          notification({
            type: 'error',
            title: message,
          })
        },
      }
    )
  }

  const { data: internalRolesData, isLoading: isRolesLoading } = useInternalRoles()
  const roles = internalRolesData?.data || []

  const onSubmit = handleSubmit(data => {
    if (!data.role) {
      notification({
        type: 'error',
        title: 'Please select a role',
      })
      return
    }

    const payload = {
      role_name: data.role,
      email: data.email,
      company_ids: getCompanyIds(data),
    }
    handleInvite(payload)
  })

  const getCompanyIds = (data: any) => {
    switch (true) {
      case isPlatformAdmin:
        return undefined
      case isPlatformManager:
        return data.company_ids
      case !!companyId:
        return [companyId]
      case !!params?.id:
        return [params?.id]
    }
  }

  const onChangeCompany = (value: string[]) => {
    setValue('company_ids', value)
  }

  const internalOnClose = () => {
    onClose()
    methods.reset()
  }

  const getRoleOptions = (roles: Role[]) => {
    const filteredRoles = roles?.filter(role => role.name.includes(type))
    return (
      filteredRoles?.map(role => ({
        label: ROLES[role.name] ?? role.name,
        value: role.name,
      })) ?? []
    )
  }

  const roleOptions: SelectItem[] = React.useMemo(() => getRoleOptions(roles), [roles, type])

  return (
    <Modal
      title="Invite user"
      open={open}
      onClose={internalOnClose}
      actions={
        <>
          <Button variant="secondary" onClick={internalOnClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading} form="invite-user-form">
            Send Invite
          </Button>
        </>
      }
    >
      <Stack component="form" id="invite-user-form" onSubmit={onSubmit} spacing={4} mt={2}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Email
          </Typography>
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Enter a valid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                placeholder="name@vera.inc"
                type="email"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Role
          </Typography>
          <Controller
            name="role"
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  options={roleOptions}
                  value={field.value}
                  placeholder="Select a role"
                  disabled={isRolesLoading || roleOptions.length === 0}
                  onChange={value => field.onChange(value)}
                />
                {fieldState.error && (
                  <Typography variant="body4" color="error">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Stack>

        {isPlatformManager && (
          <SelectCompanyField
            onChange={onChangeCompany}
            defaultValue={watch('company_ids')}
          />
        )}
      </Stack>
    </Modal>
  )
}
